// Vipps error code mapping to user-friendly Norwegian messages
// Maps Vipps-specific error codes to actionable user messages

export const VippsErrorCodes = {
    // User cancellation
    'access_denied': {
        message: 'Du avbrøt innloggingen i Vipps.',
        userAction: 'Prøv igjen og fullfør innloggingen i Vipps-appen.',
        retryable: true,
        severity: 'info'
    },
    
    // Wrong challenge code
    'wrong_challenge': {
        message: 'Feil kode ble valgt i Vipps.',
        userAction: 'Prøv igjen og velg riktig kode i Vipps-appen.',
        retryable: true,
        severity: 'warning'
    },
    
    // Expired authorization code
    'expired_code': {
        message: 'Innloggingskoden har utløpt.',
        userAction: 'Skan QR-koden på nytt for å starte en ny innlogging.',
        retryable: true,
        severity: 'warning'
    },
    
    // Invalid authorization code
    'invalid_code': {
        message: 'Innloggingskoden er ugyldig.',
        userAction: 'Skan QR-koden på nytt for å starte en ny innlogging.',
        retryable: true,
        severity: 'warning'
    },
    
    // Code already used
    'code_already_used': {
        message: 'Innloggingskoden er allerede brukt.',
        userAction: 'Skan QR-koden på nytt for å starte en ny innlogging.',
        retryable: true,
        severity: 'warning'
    },
    
    // Server error from Vipps
    'server_error': {
        message: 'Vipps-tjenesten er midlertidig utilgjengelig.',
        userAction: 'Vennligst prøv igjen om noen minutter.',
        retryable: true,
        severity: 'error'
    },
    
    // Network/timeout error
    'network_error': {
        message: 'Kunne ikke koble til Vipps-tjenesten.',
        userAction: 'Sjekk internettforbindelsen din og prøv igjen.',
        retryable: true,
        severity: 'error'
    },
    
    // Backend unavailable
    'backend_unavailable': {
        message: 'Serveren er midlertidig utilgjengelig.',
        userAction: 'Vennligst prøv igjen senere.',
        retryable: true,
        severity: 'error'
    },
    
    // Timeout
    'timeout': {
        message: 'Innloggingen tok for lang tid.',
        userAction: 'Prøv igjen. Hvis problemet vedvarer, skan QR-koden på nytt.',
        retryable: true,
        severity: 'warning'
    },
    
    // Generic error
    'unknown_error': {
        message: 'En uventet feil oppstod under innlogging.',
        userAction: 'Vennligst prøv igjen. Hvis problemet vedvarer, kontakt support.',
        retryable: true,
        severity: 'error'
    }
};

/**
 * Gets user-friendly error message for a Vipps error code
 * @param {string} errorCode - The error code from Vipps
 * @param {string} defaultMessage - Default message if code not found
 * @returns {Object} Error information object
 */
export function getVippsErrorMessage(errorCode, defaultMessage = 'En feil oppstod under innlogging.') {
    if (!errorCode) {
        return {
            message: defaultMessage,
            userAction: 'Prøv igjen.',
            retryable: true,
            severity: 'error'
        };
    }
    
    // Normalize error code (lowercase, trim)
    const normalizedCode = errorCode.toLowerCase().trim();
    
    // Check for exact match
    if (VippsErrorCodes[normalizedCode]) {
        return VippsErrorCodes[normalizedCode];
    }
    
    // Check for partial matches (e.g., "expired" in error message)
    if (normalizedCode.includes('expired') || normalizedCode.includes('utløpt')) {
        return VippsErrorCodes['expired_code'];
    }
    
    if (normalizedCode.includes('invalid') || normalizedCode.includes('ugyldig')) {
        return VippsErrorCodes['invalid_code'];
    }
    
    if (normalizedCode.includes('already') || normalizedCode.includes('allerede')) {
        return VippsErrorCodes['code_already_used'];
    }
    
    if (normalizedCode.includes('denied') || normalizedCode.includes('avbrutt')) {
        return VippsErrorCodes['access_denied'];
    }
    
    if (normalizedCode.includes('wrong') || normalizedCode.includes('feil')) {
        return VippsErrorCodes['wrong_challenge'];
    }
    
    if (normalizedCode.includes('timeout') || normalizedCode.includes('tidsavbrudd')) {
        return VippsErrorCodes['timeout'];
    }
    
    if (normalizedCode.includes('network') || normalizedCode.includes('nettverk')) {
        return VippsErrorCodes['network_error'];
    }
    
    // Default to unknown error
    return {
        ...VippsErrorCodes['unknown_error'],
        message: defaultMessage || VippsErrorCodes['unknown_error'].message
    };
}

/**
 * Checks if an error is retryable
 * @param {string} errorCode - The error code
 * @returns {boolean} True if error is retryable
 */
export function isErrorRetryable(errorCode) {
    const errorInfo = getVippsErrorMessage(errorCode);
    return errorInfo.retryable !== false;
}

